/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Piano } from 'lucide-react';
import { NoteCollection } from '../api';
import { getIntervalColor } from '../config/colorScheme';

interface KeyboardProps {
  noteCollection: NoteCollection | null;
}

const Keyboard: React.FC<KeyboardProps> = ({ noteCollection }) => {
  // Detect mobile for responsive sizing
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  const allNotes = ['C', 'C#/Db', 'D', 'D#/Eb', 'E', 'F', 'F#/Gb', 'G', 'G#/Ab', 'A', 'A#/Bb', 'B'];
  const rootNote = noteCollection?.notesAndIntervals.find(ni => ni.interval.abbreviation === 'R')?.note.note;

  // Smart key ordering starting from root note
  const keys = rootNote
    ? [...allNotes.slice(allNotes.findIndex(n => n.includes(rootNote.charAt(0)))), ...allNotes].slice(0, 12)
    : allNotes;

  const isBlackKey = (note: string) => note.includes('#') || note.includes('b');

  const getNoteFromCollection = (note: string) => {
    return noteCollection?.notesAndIntervals.find(ni => {
      const noteName = ni.note.note;
      return note.split('/').some(n => n === noteName);
    });
  };

  const getKeyColor = (note: string) => {
    const collectionNote = getNoteFromCollection(note);
    if (collectionNote) {
      return getIntervalColor(collectionNote.interval.abbreviation);
    }
    return isBlackKey(note) ? '#333' : 'white';
  };

  const getDisplayNote = (note: string) => {
    const collectionNote = getNoteFromCollection(note);
    return collectionNote ? collectionNote.note.note : note.split('/')[0];
  };

  const getIntervalDisplay = (note: string) => {
    const collectionNote = getNoteFromCollection(note);
    return collectionNote?.interval.abbreviation;
  };

  // Separate white and black keys
  const whiteKeys = keys.filter(note => !isBlackKey(note));
  const blackKeys = keys.filter(note => isBlackKey(note));

  // Responsive SVG dimensions
  const keyboardWidth = isMobile ? 320 : 400;
  const keyboardHeight = isMobile ? 80 : 120;
  const whiteKeyWidth = keyboardWidth / whiteKeys.length;
  const blackKeyWidth = whiteKeyWidth * 0.6;
  const blackKeyHeight = keyboardHeight * 0.6;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Piano className="h-4 w-4" />
          Piano Keyboard
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Scale/Chord Info */}
        {noteCollection && (
          <div className="mb-3 flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {noteCollection.notesAndIntervals.length} notes
            </Badge>
            <Badge variant="secondary" className="text-xs">
              Root: {rootNote}
            </Badge>
          </div>
        )}

        {/* Piano Keyboard SVG */}
        <div className="w-full flex justify-center">
          <svg
            width={keyboardWidth}
            height={keyboardHeight}
            viewBox={`0 0 ${keyboardWidth} ${keyboardHeight}`}
            className="border-2 border-gray-400 rounded-lg shadow-sm bg-background"
          >
            {/* White keys */}
            {whiteKeys.map((note, index) => {
              const x = index * whiteKeyWidth;
              const color = getKeyColor(note);
              const textColor = color === 'white' ? 'black' : 'white';
              const interval = getIntervalDisplay(note);

              return (
                <g key={`white-${note}-${index}`}>
                  {/* Key body */}
                  <rect
                    x={x}
                    y={0}
                    width={whiteKeyWidth - 1}
                    height={keyboardHeight}
                    fill={color}
                    stroke="#e5e7eb"
                    strokeWidth="1"
                    className="drop-shadow-sm"
                  />
                  
                  {/* Note name */}
                  <text
                    x={x + whiteKeyWidth / 2}
                    y={keyboardHeight - (isMobile ? 12 : 18)}
                    textAnchor="middle"
                    fontSize={isMobile ? "10" : "12"}
                    fill={textColor}
                    fontWeight="bold"
                    className="pointer-events-none select-none"
                  >
                    {getDisplayNote(note)}
                  </text>
                  
                  {/* Interval indicator */}
                  {interval && (
                    <text
                      x={x + whiteKeyWidth / 2}
                      y={keyboardHeight - (isMobile ? 25 : 35)}
                      textAnchor="middle"
                      fontSize={isMobile ? "8" : "9"}
                      fill={textColor}
                      fontWeight="600"
                      className="pointer-events-none select-none opacity-90"
                    >
                      {interval}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Black keys */}
            {blackKeys.map((note, blackIndex) => {
              // Map black keys to their positions between white keys
              const blackKeyPositions: { [key: string]: number } = {
                'C#': 0, 'D#': 1, 'F#': 3, 'G#': 4, 'A#': 5
              };

              const noteKey = note.split('/')[0];
              const whiteKeyIndex = blackKeyPositions[noteKey];

              if (whiteKeyIndex === undefined) return null;

              // Position black key between white keys
              const x = (whiteKeyIndex + 0.7) * whiteKeyWidth;
              const color = getKeyColor(note);
              const textColor = color === '#333' ? 'white' : 'black';
              const interval = getIntervalDisplay(note);

              return (
                <g key={`black-${note}-${blackIndex}`}>
                  {/* Key body */}
                  <rect
                    x={x}
                    y={0}
                    width={blackKeyWidth}
                    height={blackKeyHeight}
                    fill={color}
                    stroke="#374151"
                    strokeWidth="1"
                    className="drop-shadow-md"
                  />
                  
                  {/* Note name */}
                  <text
                    x={x + blackKeyWidth / 2}
                    y={blackKeyHeight - (isMobile ? 8 : 12)}
                    textAnchor="middle"
                    fontSize={isMobile ? "8" : "9"}
                    fill={textColor}
                    fontWeight="bold"
                    className="pointer-events-none select-none"
                  >
                    {getDisplayNote(note)}
                  </text>
                  
                  {/* Interval indicator */}
                  {interval && (
                    <text
                      x={x + blackKeyWidth / 2}
                      y={blackKeyHeight - (isMobile ? 18 : 25)}
                      textAnchor="middle"
                      fontSize={isMobile ? "7" : "8"}
                      fill={textColor}
                      fontWeight="600"
                      className="pointer-events-none select-none opacity-90"
                    >
                      {interval}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Legend */}
        {noteCollection && (
          <div className="mt-3 text-center">
            <p className="text-xs text-muted-foreground">
              Keys are colored by interval and show scale/chord notes
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Keyboard;
